import { useCallback } from "react";

import { DateTime } from "luxon";

import {
  generateRecurrenceRuleString,
  FREQUENCIES,
} from "../../../../Recurrence";
import { useObjectMemo } from "../../../hooks";
import {
  useRequestWithFeedback,
  combineRunProps,
  type OverrideRunProps,
  useIdempotencyErrorHandling,
  parseErrorJson,
  parseErrorResponse,
  type ErrorResponse,
  type AnyException,
  type Data,
} from "../../../composites";
import { useLibrary } from "../../../providers";
import { useAppDispatch } from "../utils";
import { useACHCompany } from "../achCompanies";
import { useInstitution } from "../institution";

import { API } from "./api";
import { actions } from "./slice";
import { BulkPaymentForm } from "./BulkPayment.form";
import { getScreenWithError } from "./helpers";
import { EXPEDITE_CHOICE_MAPPER } from "./bulkBase";

export const useFetchPaginatedBulkPayments = () => {
  const { send, loading, status } = useRequestWithFeedback<
    { results: API.BulkPayment[]; page: { totalElements: number } },
    unknown
  >();

  const dispatch = useAppDispatch();

  const fetchPage = useCallback(
    (
      pageNumber: number,
      offsetIndex: number,
      pageSize: number,
      queryString?: string,
      ordering?: string,
    ) => {
      send({
        action: API.fetchBulkPayments(
          offsetIndex,
          pageSize,
          queryString,
          ordering,
        ),
        onData: ({ results, page: { totalElements } }) => {
          dispatch(
            actions.upsertPage({
              pageNumber,
              results,
              total: totalElements,
            }),
          );
        },
      });
    },
    [send, dispatch],
  );

  return useObjectMemo({
    error: status === "error",
    send: fetchPage,
    loading,
  });
};

export const useFetchBulkPayments = () => {
  const { send, loading, status } = useRequestWithFeedback<
    { results: API.BulkPayment[]; page: { totalElements: number } },
    unknown
  >();

  const dispatch = useAppDispatch();

  const fetchNext = useCallback(
    (
      offsetIndex: number,
      pageSize: number,
      queryString?: string,
      ordering?: string,
    ) => {
      send({
        action: API.fetchBulkPayments(
          offsetIndex,
          pageSize,
          queryString,
          ordering,
        ),
        onData: ({ results, page: { totalElements } }) => {
          if (offsetIndex === 0) {
            dispatch(actions.replaceResults({ results, total: totalElements }));
          } else {
            dispatch(
              actions.appendResults({
                results,
                total: totalElements,
              }),
            );
          }
        },
      });
    },
    [send, dispatch],
  );

  return useObjectMemo({
    error: status === "error",
    send: fetchNext,
    loading,
  });
};

export const useFetchBulkPaymentById = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.BulkPayment,
    unknown
  >();

  const dispatch = useAppDispatch();

  const fetchBulkPaymentById = useCallback(
    (uuid: API.BulkPaymentId) => {
      return new Promise<API.BulkPayment>((resolve, reject) => {
        send({
          action: API.fetchBulkPaymentById(uuid),
          onData: (data) => {
            dispatch(actions.updateSelected(data));
            resolve(data);
            return data;
          },
          onError: (err) => {
            reject(err);
          },
        });
      });
    },
    [send, dispatch],
  );

  return useObjectMemo({
    error: status === "error",
    send: fetchBulkPaymentById,
    loading,
  });
};

export const useCreateBulkPayment = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.CreateBulkPayment.Response,
    API.CreateBulkPayment.Error
  >();
  const { values } = BulkPaymentForm.useForm();
  const achCompany = useACHCompany();
  const institution = useInstitution();
  const {
    expedited_payments: expeditedPaymentsFeature,
    ach_payments_collect: achPaymentsCollectFeature,
  } = institution?.features ?? {};
  const canHandleIdempotencyErrors = useIdempotencyErrorHandling(
    "payment-idempotency-error",
  );

  const createBulkPayment = useCallback(
    (
      idempotencyKey: UUID,
      overrideRunProps: {
        onSuccess?: () => void;
        onError?: (error: any) => void;
      } = {},
      deviceId?: string,
      deviceRequestId?: string,
      organizationFileUuid?: string,
    ) => {
      const {
        title,
        fromAccount,
        secCode,
        description,
        entryDesc,
        groupPosting,
        payments,
        chosenExpediteOption,
      } = values;

      const formattedPayments = payments.map((payment) => {
        const {
          scheduledDate,
          recipient,
          description: paymentDescription,
          amount,
          speedStatusWithExpediteOption,
          transactionType,
        } = payment;

        const formattedDate = DateTime.fromFormat(
          scheduledDate,
          "M/d/yyyy",
        ).toFormat("yyyy-MM-dd");

        const rrule = generateRecurrenceRuleString(
          FREQUENCIES.ONCE,
          formattedDate,
          null,
          null,
        );

        return {
          recipient: recipient?.id,
          description: paymentDescription,
          amount,
          ...(rrule && { recurring_rule: rrule }),
          // chosenExpediteOption and speedStatusWithExpediteOption would not exist if expedited_payments
          // isnt enabled, but adding extra guard to be safe
          ...(expeditedPaymentsFeature &&
            chosenExpediteOption &&
            speedStatusWithExpediteOption && {
              expedite_choice:
                EXPEDITE_CHOICE_MAPPER[speedStatusWithExpediteOption],
            }),
          ...(achPaymentsCollectFeature && {
            transaction_type:
              transactionType === "collect" ? "debit" : "credit",
          }),
        };
      });

      const formattedValues = {
        title,
        institution_account: fromAccount as API.AccountId,
        sec_code: secCode,
        description,
        entry_desc: entryDesc,
        group_posting: groupPosting,
        payments: formattedPayments,
        company: achCompany?.id,
        device_id: deviceId,
        device_request_id: deviceRequestId,
        ...(organizationFileUuid && {
          organization_file_uuid: organizationFileUuid,
        }),
      };

      send({
        action: API.createBulkPayment(idempotencyKey, formattedValues),
        onSuccess: overrideRunProps.onSuccess,
        onError: async (error) => {
          const json = await error?.response?.json().catch(() => undefined);

          const { errors } = parseErrorJson(json);
          if (canHandleIdempotencyErrors(errors)) return;

          const screenWithError = getScreenWithError(json);
          overrideRunProps.onError?.(screenWithError);
        },
      });
    },
    [
      send,
      values,
      achCompany,
      expeditedPaymentsFeature,
      achPaymentsCollectFeature,
      canHandleIdempotencyErrors,
    ],
  );

  return useObjectMemo({
    error: status === "error",
    createBulkPayment,
    loading,
  });
};

export const useDeleteBulkPayment = () => {
  const { send, loading } = useRequestWithFeedback();
  const l10n = useLibrary("translations");
  const { throwToast } = useLibrary("toasts");

  const deleteBulkPayment = useCallback(
    (uuid: API.BulkPaymentId, overrideRunProps: OverrideRunProps = {}) => {
      const runProps = combineRunProps(
        {
          onSuccess: async () => {
            throwToast({
              kind: "success",
              message: l10n.getString(
                "delete-payment-success-message",
                null,
                "Payment canceled.",
              ),
            });
          },
          onError: async (error: ErrorResponse<AnyException<Data>>) => {
            const { errors } = await parseErrorResponse(error);
            const defaultError = l10n.getString(
              "delete-payment-error-message",
              null,
              "We could not cancel your payment. Please try again.",
            );
            throwToast({
              kind: "error",
              message: errors.length
                ? `${errors[0].description}`
                : defaultError,
            });
          },
        },
        overrideRunProps,
      );

      try {
        send({
          action: API.deleteBulkPayment(uuid),
          ...runProps,
        });
      } catch (error) {
        runProps.onError?.(
          error as ErrorResponse<AnyException<Data>>,
          () => null,
        );
        runProps.onFinally?.();
      }
    },
    [send, l10n, throwToast],
  );

  return useObjectMemo({
    deleteBulkPayment,
    loading,
  });
};

export const useApproveBulkPayment = () => {
  const { send, loading, status } = useRequestWithFeedback<
    any, // TODO: replace with API.ApproveBulkPayment.Response
    any // TODO: replace with API.ApproveBulkPayment.Error
  >();

  const approveBulkPayment = useCallback(
    (uuid: API.BulkPaymentId) => {
      return send({
        action: API.approveBulkPayment(uuid),
        onData: (data) => {
          return data;
        },
      });
    },
    [send],
  );

  return useObjectMemo({
    error: status === "error",
    send: approveBulkPayment,
    loading,
  });
};

export const useCancelBulkPayment = () => {
  const { send, loading, status } = useRequestWithFeedback<
    any, // TODO: replace with API.CancelBulkPayment.Response
    any // TODO: replace with API.CancelBulkPayment.Error
  >();

  const cancelBulkPayment = useCallback(
    (uuid: API.BulkPaymentId) => {
      return send({
        action: API.cancelBulkPayment(uuid),
        onData: (data) => {
          return data;
        },
      });
    },
    [send],
  );

  return useObjectMemo({
    error: status === "error",
    send: cancelBulkPayment,
    loading,
  });
};
